import { h } from "preact";
import { useEffect, useCallback } from "preact/hooks";

/**
 * Лайтбокс — модалка с увеличенным фото.
 *
 * Пропсы:
 *   photos   — массив фото (уже отфильтрованный)
 *   index    — индекс открытого фото
 *   onClose  — () => void
 *   onChange — (index) => void
 */
export function Lightbox({ photos, index, onClose, onChange }) {
  const photo = photos[index];
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  const prev = useCallback(() => {
    if (hasPrev) onChange(index - 1);
  }, [index, hasPrev, onChange]);

  const next = useCallback(() => {
    if (hasNext) onChange(index + 1);
  }, [index, hasNext, onChange]);

  // Блокируем скролл body, пока открыт лайтбокс
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);

  // Обработка клавиатуры
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  // Свайп на тач-устройствах
  useEffect(() => {
    let startX = 0;
    const onTouchStart = (e) => { startX = e.changedTouches[0].clientX; };
    const onTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 50) return;
      if (dx < 0) next();
      else prev();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [prev, next]);

  return h("div", {
    class: "lightbox",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Просмотр фото",
  },
    // Фон — клик закрывает
    h("div", { class: "lightbox__backdrop", onClick: onClose }),

    // Кнопка закрытия
    h("button", {
      type: "button",
      class: "lightbox__close",
      onClick: onClose,
      "aria-label": "Закрыть",
    },
      h("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "stroke-width": "2", stroke: "currentColor" },
        h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", d: "M6 18L18 6M6 6l12 12" })
      )
    ),

    // Стрелка назад
    hasPrev && h("button", {
      type: "button",
      class: "lightbox__arrow lightbox__arrow--prev",
      onClick: prev,
      "aria-label": "Предыдущее фото",
    },
      h("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "stroke-width": "2", stroke: "currentColor" },
        h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", d: "M15.75 19.5 8.25 12l7.5-7.5" })
      )
    ),

    // Фото
    h("div", { class: "lightbox__content" },
      h("img", {
        class: "lightbox__image",
        src: photo.src,
        alt: photo.alt,
        width: photo.width,
        height: photo.height,
      }),
      photo.title && h("p", { class: "lightbox__caption" },
        photo.title,
        h("span", { class: "lightbox__counter" }, ` · ${index + 1} / ${photos.length}`)
      )
    ),

    // Стрелка вперёд
    hasNext && h("button", {
      type: "button",
      class: "lightbox__arrow lightbox__arrow--next",
      onClick: next,
      "aria-label": "Следующее фото",
    },
      h("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "stroke-width": "2", stroke: "currentColor" },
        h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", d: "m8.25 4.5 7.5 7.5-7.5 7.5" })
      )
    )
  );
}
